import {toAbsoluteUrl} from '_metronic/helpers'
import {useEffect, useState} from 'react'
import {useParams, Link, useLocation} from 'react-router-dom'
import ComplianceTab from './tabs/ComplianceTab'
import RequestAccessModal from './components/modals/RequestAccessModal'
import ResourcesTab from './tabs/ResourcesTab'
import ControlsTab from './tabs/ControlsTab'
import {ToastContainer} from 'react-toastify'
import {Helmet} from 'react-helmet'
import db from 'services/firebase'
import {collection, doc, onSnapshot, orderBy, query} from '@firebase/firestore'
import {PolicyDataType} from './components/Policies'
import {FrameworkDataType} from './components/Compliance'
import {DocumentDataType} from './components/Documents'
import {ControlDataType} from './components/Controls'
import Button from './components/Button'
import SubscribeModal from './components/modals/SubscribeModal'
import {HeaderDataType} from './components/Header'
import {SubprocessorDataType} from './components/Subprocessors'
import SubprocessorsTab from './tabs/SubprocessorsTab'
import {decryptId} from './utils/helpers'
import {FAQDataType} from './components/FAQ'
import FAQTab from './tabs/FAQTab'

type HeaderDetailsType = HeaderDataType & {companyName: string; companyLogoURL: string}

const PublishedTrustCenter = () => {
  const {companyId} = useParams()

  const location = useLocation()

  const tabs: Array<{title: string; href: string; slug: string}> = [
    {
      title: 'Compliance',
      slug: 'compliance',
      href: `/trust/${companyId}`,
    },
    {
      title: 'Controls',
      slug: 'controls',
      href: `/trust/${companyId}/controls`,
    },
    {
      title: 'Resources',
      slug: 'resources',
      href: `/trust/${companyId}/resources`,
    },
    {
      title: 'Subprocessors',
      slug: 'subprocessors',
      href: `/trust/${companyId}/subprocessors`,
    },
    {
      title: 'FAQ',
      slug: 'faq',
      href: `/trust/${companyId}/faq`,
    },
  ]

  const [tenantId, setTenantId] = useState('')
  const [encryptedTenantId, setEncryptedTenantId] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isDataRetrieved, setIsDataRetrieved] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false)

  const [headerDetails, setHeaderDetails] = useState<HeaderDetailsType>()
  const [policies, setPolicies] = useState<PolicyDataType[]>()
  const [documents, setDocuments] = useState<DocumentDataType[]>([])
  const [controls, setControls] = useState<ControlDataType[]>()
  const [frameworks, setFrameworks] = useState<FrameworkDataType[]>()
  const [resources, setResources] = useState<{id: string; title: string; documentURL: string}[]>([])
  const [subprocessors, setSubprocessors] = useState<SubprocessorDataType[]>([])
  const [FAQs, setFAQs] = useState<FAQDataType[]>([])
  const [requestedResource, setRequestedResource] = useState<{id: string}>({id: ''})
  const [requestedResourcesIdArr, setRequestedResourcesIdArr] = useState<string[]>([])

  useEffect(() => {
    const fetchTenantId = async () => {
      try {
        const domainsRef = collection(db, 'trust_center_domains')

        const unsubscribe = onSnapshot(domainsRef, (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            ...doc.data(),
          })) as {encryptedTenantId: string; trustCenterURL: string}[]

          const filteredData = data.filter((item) => {
            const parsedUrl = new URL(item?.trustCenterURL)
            const pathParts = parsedUrl.pathname.split('/')
            const domain = pathParts[pathParts.length - 1]
            return domain === companyId
          })

          if (filteredData.length === 0) window.location.href = '/error/404'
          else {
            setTenantId(decryptId(filteredData[0].encryptedTenantId))
            setEncryptedTenantId(filteredData[0].encryptedTenantId)
          }
        })

        return unsubscribe
      } catch (error) {
        console.log('Error while getting data for published trust center', error)
      }
    }

    fetchTenantId()
  }, [])

  const getHeaderDetails = async () => {
    try {
      const detailsRef = doc(db, 'tenants', tenantId, 'trust_center', 'headerDetails')

      onSnapshot(detailsRef, (docSnap) => {
        if (docSnap.exists()) {
          setHeaderDetails({
            ...(docSnap.data() as HeaderDetailsType),
          })
        } else {
          window.location.href = '/error/404'
        }
      })
    } catch (error) {
      console.log('Error while getting header details for published trust center:', error)
    }
  }

  const getAddedPolicies = async () => {
    try {
      const policiesRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'policies',
        'all_policies'
      )

      const queryRef = query(policiesRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const policies = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as PolicyDataType[]

        setPolicies(policies)
        setResources((prev) => {
          return [
            ...policies
              .filter((policy) => policy.access.toLowerCase() === 'restricted')
              .map((policy) => ({
                id: policy.id,
                title: policy.title,
                documentURL: policy.documentURL as string,
              })),
          ]
        })
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting policies details for published trust center:', error)
    }
  }

  const getAddedDocuments = async () => {
    try {
      const documentsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'documents',
        'all_documents'
      )

      const queryRef = query(documentsRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const documents = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as DocumentDataType[]

        setDocuments(documents)
        setResources((prev) => {
          return [
            ...prev,
            ...documents
              .filter((document) => document.access.toLowerCase() === 'restricted')
              .map((document) => ({
                id: document.id,
                title: document.title,
                documentURL: document.documentURL as string,
              })),
          ]
        })
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting documents details for published trust center:', error)
    }
  }

  const getAddedFrameworks = async () => {
    try {
      const frameworksRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'frameworks',
        'all_frameworks'
      )

      const queryRef = query(frameworksRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const frameworks = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as FrameworkDataType[]
        setFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting frameworks details for published trust center:', error)
    }
  }

  const getControls = async () => {
    try {
      const controlsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'controls',
        'all_controls'
      )

      const queryRef = query(controlsRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const controls = snapshot.docs
          .filter((doc) => doc.data()?.isShownInTrustCenter)
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as ControlDataType[]
        setControls(controls)
      })

      return unsubscribe
    } catch (error) {
      console.error('Errow while getting controls for published trust center', error)
    } finally {
      // setIsLoading(false)
      setIsDataRetrieved(true)
    }
  }

  const getAddedSubprocessors = async () => {
    try {
      const subprocessorsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'subprocessors',
        'all_subprocessors'
      )

      const queryRef = query(subprocessorsRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const subprocessors = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as SubprocessorDataType[]
        setSubprocessors(subprocessors)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting subprocessors details for trust center:', error)
    }
  }

  const getAddedFAQs = async () => {
    try {
      const faqsFef = collection(db, 'tenants', tenantId, 'trust_center', 'faqs', 'all_faqs')

      const queryRef = query(faqsFef, orderBy('updatedAt', 'desc'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const faqs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as FAQDataType[]
        setFAQs(faqs)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting faqs for trust center:', error)
    }
  }

  useEffect(() => {
    if (tenantId) {
      getHeaderDetails()
    }
  }, [tenantId])

  useEffect(() => {
    if (tenantId) {
      getAddedPolicies()
      getAddedDocuments()
      getAddedSubprocessors()
      getAddedFrameworks()
      getAddedFAQs()
      getControls()
    }
  }, [location.pathname, tenantId])

  useEffect(() => {
    let timeoutId: any
    if (isDataRetrieved) {
      timeoutId = setTimeout(() => {
        setIsLoading(false) // show loading for 2 seconds more even though data has been retrieved as it takes time to load images
      }, 2000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isDataRetrieved])

  const [activeTab, setActiveTab] = useState(
    location.pathname.split('/').length === 2 ? tabs[0].slug : location.pathname.split('/').at(-1)
  )

  const renderTab = () => {
    switch (activeTab) {
      case 'compliance':
        return <ComplianceTab frameworks={frameworks!} />
      case 'controls':
        return <ControlsTab controls={controls!} />
      case 'resources':
        return (
          <ResourcesTab
            policies={policies!}
            documents={documents!}
            openRequestAccessModal={() => setIsModalOpen(true)}
            setRequestedResource={setRequestedResource}
            requestedResourcesIdArr={requestedResourcesIdArr}
          />
        )
      case 'subprocessors':
        return <SubprocessorsTab subprocessors={subprocessors!} />
      case 'faq':
        return <FAQTab FAQs={FAQs!} />
    }
  }

  useEffect(() => {
    let isFirstTab = true

    for (const tab of tabs) {
      if (location.pathname.includes(tab.slug)) {
        setActiveTab(tab.slug)
        isFirstTab = false
        break
      }
    }

    if (isFirstTab) setActiveTab(tabs[0].slug)
  }, [location.pathname])

  return (
    <>
      {isModalOpen && (
        <RequestAccessModal
          isModalOpen={isModalOpen}
          hideModal={() => setIsModalOpen(false)}
          encryptedTenantId={encryptedTenantId}
          resources={resources!}
          requestedResource={requestedResource}
          setRequestedResourcesIdArr={setRequestedResourcesIdArr}
          companyName={headerDetails?.companyName || ''}
          masterServiceAgreementLink={headerDetails?.masterServiceAgreement || ''}
        />
      )}

      {isSubscribeModalOpen && (
        <SubscribeModal
          isModalOpen={isSubscribeModalOpen}
          hideModal={() => setIsSubscribeModalOpen(false)}
          encryptedTenantId={encryptedTenantId}
          companyName={headerDetails?.companyName || 'our company'}
          privacyPolicyLink={headerDetails?.privacyPolicy || ''}
        />
      )}

      <Helmet>
        <title>Trust Center</title>
        <link rel='shortcut icon' href='/media/icon/trust-center.svg' />
      </Helmet>

      {isLoading ? (
        <div className='min-vh-100 d-flex flex-column'>
          <div
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        </div>
      ) : (
        <div className='min-vh-100 position-relative overflow-y-scroll'>
          <div style={{height: '245px', backgroundColor: '#262626'}}></div>

          <div
            className='w-100 w-lg-75 position-absolute d-flex flex-column'
            style={{
              maxWidth: '1280px',
              top: '64px',
              left: '50%',
              translate: '-50% 0',
              gap: '32px',
            }}
          >
            {/* header goes here  */}
            <div
              className='d-flex flex-column bg-white mx-6 mx-lg-0'
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                gap: '24px',
                padding: '32px 24px',
              }}
            >
              <div className='d-flex align-items-center' style={{gap: '16px'}}>
                <img
                  alt='company-logo'
                  src={toAbsoluteUrl(
                    headerDetails?.companyLogoURL || '/media/svg/avatars/blank.svg'
                  )}
                  className='object-fit-cover'
                  style={{height: '56px', width: '56px'}}
                />

                <span
                  className='text-custom-primary-gray'
                  style={{fontSize: '28px', fontWeight: '600', lineHeight: '32px'}}
                >
                  {headerDetails?.title}
                </span>

                <div className='ms-auto d-flex align-items-center' style={{gap: '12px'}}>
                  <div className='d-md-none'>
                    <Button
                      onClick={() => {
                        setIsSubscribeModalOpen(true)
                      }}
                      style={{
                        height: '40px',
                        width: '40px',
                      }}
                    >
                      <img
                        alt='bell-ringing-icon'
                        src={toAbsoluteUrl('/media/icon/bell-ringing.svg')}
                        className='me-2'
                        style={{height: '16px', width: '16px'}}
                      />
                    </Button>
                  </div>

                  <div className='d-none d-md-block'>
                    <Button
                      onClick={() => {
                        setIsSubscribeModalOpen(true)
                      }}
                    >
                      <img
                        alt='bell-ringing-icon'
                        src={toAbsoluteUrl('/media/icon/bell-ringing.svg')}
                        className='me-2'
                        style={{height: '16px', width: '16px'}}
                      />
                      <span>Subscribe To Updates</span>
                    </Button>
                  </div>

                  <div className='d-none d-md-block'>
                    <Button
                      btnType='black'
                      className=''
                      onClick={() => {
                        setRequestedResource({id: ''})
                        setIsModalOpen(true)
                      }}
                    >
                      Request Access
                    </Button>
                  </div>
                </div>
              </div>

              <p className='text-custom-primary-gray mb-0' style={{fontSize: '16px'}}>
                {headerDetails?.description}
              </p>

              <div>
                <div className='d-flex flex-wrap' style={{gap: '14px', width: '50%'}}>
                  {headerDetails?.masterServiceAgreement.trim() !== '' && (
                    <a
                      href={headerDetails?.masterServiceAgreement}
                      target='_blank'
                      className='d-flex align-items-center'
                      style={{gap: '6px'}}
                    >
                      <img
                        alt='link-icon'
                        src={toAbsoluteUrl('/media/icon/link.svg')}
                        className=''
                        style={{height: '16px', width: '16px'}}
                      />

                      <span
                        className='lh-1 text-custom-primary-gray'
                        style={{fontSize: '16px', textDecoration: 'underline'}}
                      >
                        Master Service Agreement
                      </span>
                    </a>
                  )}

                  {headerDetails?.privacyPolicy.trim() !== '' && (
                    <a
                      href={headerDetails?.privacyPolicy}
                      target='_blank'
                      className='d-flex align-items-center'
                      style={{gap: '6px'}}
                    >
                      <img
                        alt='link-icon'
                        src={toAbsoluteUrl('/media/icon/link.svg')}
                        className=''
                        style={{height: '16px', width: '16px'}}
                      />

                      <span
                        className='lh-1 text-custom-primary-gray'
                        style={{fontSize: '16px', textDecoration: 'underline'}}
                      >
                        Privacy Policy
                      </span>
                    </a>
                  )}

                  {headerDetails?.links.map(
                    (link, idx) =>
                      link.url.trim() !== '' && (
                        <a
                          key={idx}
                          href={link.url}
                          target='_blank'
                          className='d-flex align-items-center'
                          style={{gap: '6px'}}
                        >
                          <img
                            alt='link-icon'
                            src={toAbsoluteUrl('/media/icon/link.svg')}
                            className=''
                            style={{height: '16px', width: '16px'}}
                          />

                          <span
                            className='lh-1 text-custom-primary-gray'
                            style={{fontSize: '16px', textDecoration: 'underline'}}
                          >
                            {link.title}
                          </span>
                        </a>
                      )
                  )}

                  {headerDetails?.masterServiceAgreement.trim() === '' &&
                    headerDetails?.privacyPolicy.trim() === '' && (
                      <span className='text-custom-secondary-gray' style={{fontSize: '14px'}}>
                        No links available at this time
                      </span>
                    )}
                </div>
              </div>

              <div className='d-md-none'>
                <Button
                  btnType='black'
                  onClick={() => {
                    setRequestedResource({id: ''})
                    setIsModalOpen(true)
                  }}
                >
                  Request Access
                </Button>
              </div>
            </div>

            {/* toolbar goes here  */}
            <div className='mx-6 mx-lg-0' style={{borderBottom: '1px solid #D1D5DB'}}>
              <div className='d-flex align-items-center flex-nowrap' style={{gap: '24px'}}>
                {tabs.map((tab, index) => (
                  <Link
                    key={tab.slug}
                    to={tab.href}
                    className=''
                    style={{
                      borderBottom: `${
                        activeTab === tab.slug ? '2px solid #111928' : '2px solid transparent'
                      }`,
                      paddingBottom: '8px',
                    }}
                  >
                    <span
                      className={`lh-sm fw-medium ${
                        activeTab === tab.slug
                          ? 'text-custom-primary-gray'
                          : 'text-custom-secondary-gray'
                      }`}
                      style={{
                        fontSize: '14px',
                      }}
                    >
                      {tab.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* tabs goes here  */}
            <div className='mx-6 mx-lg-0'>{renderTab()}</div>

            {/* footer goes here  */}
            <div
              className='w-100 d-flex align-items-center justify-content-center'
              style={{
                height: '64px',
              }}
            >
              <span className='fw-medium lh-sm me-2' style={{color: '#4B5563', fontSize: '12px'}}>
                Monitored & Powered by
              </span>
              <a href='https://getsecureslate.com/' target='_blank'>
                <img
                  alt='Logo'
                  src={toAbsoluteUrl('/Secureslate_logos/Secureslate_dark.png')}
                  className=''
                  style={{height: '20px'}}
                />
              </a>
            </div>
          </div>

          {/* footer goes here  */}
          {/* <div
            className='w-100 d-flex align-items-center justify-content-center'
            style={{
              backgroundColor: '#f9fafb',
              height: '64px',
              borderTop: '1px solid #D1D5DB',
              zIndex: 10,
            }}
          >
            <span className='fw-medium lh-sm me-2' style={{color: '#4B5563', fontSize: '14px'}}>
              Monitored & Powered by
            </span>
            <a href='https://getsecureslate.com/' target='_blank'>
              <img
                alt='Logo'
                src={toAbsoluteUrl('/Secureslate_logos/Secureslate_dark.png')}
                className=''
                style={{height: '24px'}}
              />
            </a>
          </div> */}
        </div>
      )}

      <ToastContainer />
    </>
  )
}

export default PublishedTrustCenter
